import { useState } from 'react';
import { useList, useDelete, useCustomMutation, useInvalidate } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Archive, RotateCcw, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DataTable, TableToolbar, type BulkAction, type Column, type SortState } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { notify } from '@/lib/notify';
import type { TagItem } from './types';
import { TagCreateDialog } from './components';

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function TagsList() {
  useScrollRestoration();
  const navigate = useNavigate();
  const [page, setPage] = usePersistedState('tags:page', 1);
  const [search, setSearch] = usePersistedState('tags:search', '');
  const [sort, setSort] = usePersistedState<SortState | undefined>('tags:sort', { field: 'createdAt', order: 'desc' });
  const [includeArchived, setIncludeArchived] = usePersistedState('tags:includeArchived', false);
  const [deleteTargets, setDeleteTargets] = useState<TagItem[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [tableVersion, setTableVersion] = useState(0);
  const pageSize = 20;

  const invalidate = useInvalidate();

  const filters = [
    ...(search ? [{ field: 'search', operator: 'contains' as const, value: search }] : []),
    ...(includeArchived ? [{ field: 'includeArchived', operator: 'eq' as const, value: true }] : []),
  ];

  const listResult = useList<TagItem>({
    resource: 'tags',
    pagination: { pageSize, currentPage: page, mode: 'server' },
    filters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = (listResult.query.data?.data ?? []) as TagItem[];
  const total = listResult.query.data?.total ?? 0;
  const isLoading = listResult.query.isLoading;

  const deleteMutation = useDelete();
  const archiveMutation = useCustomMutation();

  const handleDelete = async () => {
    if (deleteTargets.length === 0) { return; }

    try {
      for (const tag of deleteTargets) {
        await deleteMutation.mutateAsync({ resource: 'tags', id: tag.id });
      }
      setDeleteTargets([]);
      setTableVersion((version) => version + 1);
      invalidate({ resource: 'tags', invalidates: ['list'] });
    } catch {
      notify.error('Ошибка удаления');
    }
  };

  const handleArchiveMany = async (tags: TagItem[], restore: boolean) => {
    const targetTags = tags.filter((tag) => restore ? tag.deletedAt !== null : tag.deletedAt === null);
    if (targetTags.length === 0) {
      notify.info(restore ? 'Выбранные теги уже активны' : 'Выбранные теги уже в архиве');
      return;
    }

    try {
      for (const tag of targetTags) {
        await archiveMutation.mutateAsync({
          url: `/tags/${tag.id}/${restore ? 'restore' : 'archive'}`,
          method: 'post',
          values: {},
        });
      }
      notify.success(restore ? `Восстановлено тегов: ${targetTags.length}` : `Архивировано тегов: ${targetTags.length}`);
      setTableVersion((version) => version + 1);
      invalidate({ resource: 'tags', invalidates: ['list'] });
    } catch {
      notify.error('Ошибка операции');
    }
  };

  const columns: Column<TagItem>[] = [
    {
      key: 'color',
      header: 'Цвет',
      width: '60px',
      render: (value) => (
        <div
          className="h-6 w-6 rounded border border-border"
          style={{ backgroundColor: String(value) }}
        />
      ),
    },
    {
      key: 'name',
      header: 'Название',
      sortable: true,
      render: (_, tag) => (
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{tag.name}</span>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Описание',
      render: (value) => (
        <span className="text-sm text-muted-foreground">
          {value ? String(value) : '—'}
        </span>
      ),
    },
    {
      key: 'deletedAt',
      header: 'Статус',
      width: '130px',
      render: (_, tag) => (
        <Badge variant={tag.deletedAt ? 'outline' : 'default'}>
          {tag.deletedAt ? 'Архивирован' : 'Активный'}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Создан',
      width: '120px',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-muted-foreground">{formatDate(String(value))}</span>
      ),
    },
  ];

  const bulkActions: BulkAction<TagItem>[] = [
    {
      label: 'Архивировать',
      icon: <Archive className="h-4 w-4" />,
      onClick: (tags) => {
        void handleArchiveMany(tags, false);
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Восстановить',
      icon: <RotateCcw className="h-4 w-4" />,
      onClick: (tags) => {
        void handleArchiveMany(tags, true);
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'destructive',
      onClick: setDeleteTargets,
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Теги"
        description="Управление тегами для пользователей"
        sticky
        icon={<Tag className="h-5 w-5" />}
        actions={
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Создать тег
          </Button>
        }
      />
      <TagCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={() => {
          invalidate({ resource: 'tags', invalidates: ['list'] });
          setPage(1);
        }}
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Поиск по названию тега..."
        filters={
          <Button
            type="button"
            variant={includeArchived ? 'secondary' : 'outline'}
            onClick={() => {
              setIncludeArchived((currentValue) => !currentValue);
              setPage(1);
            }}
            className="gap-2"
          >
            <Archive className="h-4 w-4" />
            {includeArchived ? 'Архив показан' : 'Показать архив'}
          </Button>
        }
        filtersCount={includeArchived ? 1 : 0}
        onResetFilters={() => {
          setIncludeArchived(false);
          setPage(1);
        }}
      />

      <DataTable
        key={`${includeArchived ? 'with-archive' : 'active'}-${tableVersion}`}
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(tag) => navigate(`/tags/edit/${tag.id}`)}
        emptyMessage="Нет тегов"
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteTargets.length > 0} onOpenChange={() => setDeleteTargets([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTargets.length === 1 ? 'Удалить тег?' : `Удалить тегов: ${deleteTargets.length}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Выбранные теги будут удалены навсегда.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { void handleDelete(); }}
              disabled={deleteMutation.mutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.mutation.isPending ? 'Удаление...' : 'Удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
